import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { addOwnerAsMember } from './listMembers$';
import { getArticleIdsInList } from './articleLists$';
import { articles$, deleteArticle } from './articles$';

// Lists store with Supabase sync (local-first)
// Now includes lists where user is owner OR member
export const lists$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_lists',
  // RLS handles the filtering - user sees own lists + shared lists
  filter: (select) => select,
  // Custom create/update with error handling (upsert fallback)
  create: createSyncCreate('reedle_lists'),
  update: createSyncUpdate('reedle_lists'),
  // Use default sync config
  ...defaultSyncConfig,
  persist: {
    name: 'reedle-lists',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

/**
 * Create a new list and add creator as owner member
 */
export const createList = async (name) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = uuidv4();
  const now = new Date().toISOString();

  const newList = {
    id,
    user_id: userId,
    name: name.trim(),
    created_at: now,
    updated_at: now,
  };

  // Insert directly to database first (to ensure FK constraint for members)
  const { error } = await supabase.from('reedle_lists').insert(newList);
  if (error) {
    console.error('Failed to create list:', error);
    throw new Error(error.message);
  }

  // Add to local store
  lists$[id].set(newList);

  // Add creator as owner member (for collaborative features)
  await addOwnerAsMember(id);

  return newList;
};

/**
 * Rename a list
 */
export const renameList = (listId, newName) => {
  lists$[listId].assign({
    name: newName.trim(),
    updated_at: new Date().toISOString(),
  });
};

/**
 * Delete a list (articles will be moved to Inbox via ON DELETE SET NULL)
 */
export const deleteList = (listId) => {
  lists$[listId].delete();
};

/**
 * Delete a list and permanently delete all articles in it that are owned by the current user.
 * Articles owned by other users (shared list) will be moved to Inbox via ON DELETE SET NULL.
 */
export const deleteListWithArticles = (listId) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  const articleIds = getArticleIdsInList(listId);
  for (const articleId of articleIds) {
    const article = articles$[articleId].get();
    // Only delete articles owned by the current user; others' articles go to inbox
    if (article && article.user_id === userId) {
      deleteArticle(articleId);
    }
  }

  // Delete the list itself (remaining articles from other users move to inbox via DB cascade)
  deleteList(listId);
};

/**
 * Get lists as array (sorted by created_at)
 */
export const getListsArray = () => {
  const listsObj = lists$.get() || {};
  return Object.values(listsObj)
    .filter(list => list && list.id && !list.deleted)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

/**
 * Get list by ID
 */
export const getListById = (listId) => {
  return lists$[listId].get();
};

